import React from "react";
import { Code2, List } from "lucide-react";
import { formatTimestamp } from "../utils/gatewayUtils";
import GatewayOboIntegrity from "./GatewayOboIntegrity";
import GatewayJsonViewer from "./GatewayJsonViewer";

function principalLabel(pr: any): string {
  if (!pr) return "?";
  if (typeof pr === "string") return pr;
  return pr.username || pr.clientId || (pr.id ? String(pr.id) : pr.type || "?");
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  if (children == null || children === "" || children === false) return null;
  return (
    <div className="grid grid-cols-[132px_1fr] gap-3 px-4 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold leading-relaxed">{label}</div>
      <div className="font-mono text-xs text-foreground break-all">{children}</div>
    </div>
  );
}

export default function GatewayOboReceipt({ receipt }: { receipt: any }) {
  const [raw, setRaw] = React.useState(false);
  if (!receipt) return null;
  const r = receipt;
  const chain: any[] = Array.isArray(r.act_chain) ? r.act_chain : [];
  const inv = r.obo_invariants || r.oboInvariants;
  const hasInv = !!inv && typeof inv === "object";
  const boundTo = r?.cnf?.bound_to ?? (typeof r?.cnf === "string" ? r.cnf : undefined) ?? r.aud;
  const issued = r.issuedAt
    ? `${formatTimestamp(r.issuedAt)}${r.ttlSeconds != null ? ` · ttl ${r.ttlSeconds}s` : ""}`
    : r.ttlSeconds != null
    ? `ttl ${r.ttlSeconds}s`
    : null;

  const rawBtn = (
    <button
      type="button"
      onClick={() => setRaw((v) => !v)}
      className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-primary/[0.025] transition-colors"
      title={raw ? "Show the friendly view" : "Show the raw receipt JSON"}
    >
      {raw ? (
        <><List className="w-3 h-3" /> Friendly</>
      ) : (
        <><Code2 className="w-3 h-3" /> Raw</>
      )}
    </button>
  );

  return (
    <div className="space-y-2">
      {/* integrity banner — pinned on top for both views */}
      {hasInv && <GatewayOboIntegrity receipt={r} />}

      {/* Raw / Friendly toggle — sits directly below the integrity box, left-aligned */}
      <div className="flex justify-start">{rawBtn}</div>

      {raw ? (
        <GatewayJsonViewer data={r} collapsed={false} maxHeight="45vh" />
      ) : (
        <div className="rounded-xl border border-border overflow-hidden divide-y divide-border">
          <Row label="ISS">{r.iss}</Row>
          <Row label="SUB">{r.sub}</Row>
          <Row label="AUD">{r.aud}</Row>
          <Row label="Scope">{r.scope}</Row>
          <Row label="Actor">{r.actor ? principalLabel(r.actor) : null}</Row>
          <Row label="CNF → Bound to">{boundTo}</Row>
          <Row label="JTI">{r.jti}</Row>
          <Row label="Act_chain">
            {chain.length ? chain.map((p) => principalLabel(p)).join(" → ") : null}
          </Row>
          <Row label="Issued · TTL">{issued}</Row>
          <Row label="ALG">{r.alg ? `${r.alg}${r.kid ? ` · kid ${r.kid}` : ""}` : null}</Row>
          <Row label="Trace">{r.trace_id || r.traceId}</Row>
        </div>
      )}
    </div>
  );
}
